import { useLocalStorage } from "@mantine/hooks";

import {
  DEFAULT_METRIC_TILES,
  getMetric,
  MetricGroup,
} from "../constants/metrics";

type MetricTiles = Record<MetricGroup, string[]>;

const STORAGE_KEY = "scorekeeping-v2-metric-tiles";

/** Keeps only known metric ids that belong to the group; falls back to defaults. */
const sanitizeGroup = (group: MetricGroup, ids: unknown): string[] => {
  if (!Array.isArray(ids)) return DEFAULT_METRIC_TILES[group];
  return ids.filter(
    (id): id is string =>
      typeof id === "string" && getMetric(id)?.group === group,
  );
};

// Mantine passes null when the key is missing, so validate every read
const deserialize = (raw: string | null | undefined): MetricTiles => {
  let parsed: Partial<MetricTiles> | null = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }
  return {
    pitch: sanitizeGroup("pitch", parsed?.pitch),
    hit: sanitizeGroup("hit", parsed?.hit),
  };
};

const isSameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/** Customizable metric tiles, persisted to localStorage. */
export const useMetricTiles = () => {
  const [tiles, setTiles] = useLocalStorage<MetricTiles>({
    key: STORAGE_KEY,
    defaultValue: DEFAULT_METRIC_TILES,
    deserialize,
  });

  const updateGroup = (
    group: MetricGroup,
    update: (ids: string[]) => string[],
  ) => setTiles((prev) => ({ ...prev, [group]: update(prev[group]) }));

  return {
    tiles,
    replaceTile: (group: MetricGroup, index: number, metricId: string) =>
      updateGroup(group, (ids) =>
        ids.map((id, i) => (i === index ? metricId : id)),
      ),
    hideTile: (group: MetricGroup, index: number) =>
      updateGroup(group, (ids) => ids.filter((_, i) => i !== index)),
    resetTiles: (group: MetricGroup) =>
      updateGroup(group, () => DEFAULT_METRIC_TILES[group]),
    isCustomized: (group: MetricGroup) =>
      !isSameList(tiles[group], DEFAULT_METRIC_TILES[group]),
  };
};
